/**
 * Engine-free maths the correction system recomputes after an edit, ported from the desktop:
 * RootCentroidAxis.cs (the straight valve-axis line), ComputePrincipalDiametersIndices
 * (PredictionObject.Contours.cs), the free-margin end warp (PredictionObject.FreeMargin.cs),
 * the VBR ring deform through the nadirs (OrthoPanelView.VbrFit.cs) and BezierLine.SmoothLine.
 *
 * Everything is in millimetres in one Cartesian frame; the caller picks the frame. The scene
 * frame has +Y up, which is what the plane-basis reference vector assumes (Unity Vector3.up).
 */
import * as THREE from 'three';

const TWO_PI = Math.PI * 2;

export function smootherstepFalloff(distance: number, window: number): number {
  if (window <= 1e-7) return distance <= 0 ? 1 : 0;
  const u = Math.min(1, Math.max(0, distance / window));
  return 1 - u * u * u * (u * (6 * u - 15) + 10);
}

/** Normalised arc-length parameter of each vertex (ComputeNormalizedArcParams). */
export function arcParams(points: THREE.Vector3[]): number[] {
  const out = new Array<number>(points.length).fill(0);
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += points[i - 1].distanceTo(points[i]);
    out[i] = total;
  }
  if (total <= 1e-7) return points.map((_, i) => (points.length > 1 ? i / (points.length - 1) : 0));
  for (let i = 0; i < out.length; i++) out[i] /= total;
  out[out.length - 1] = 1;
  return out;
}

export function polylineLength(points: THREE.Vector3[]): number {
  let l = 0;
  for (let i = 1; i < points.length; i++) l += points[i - 1].distanceTo(points[i]);
  return l;
}

/** Closest point on an open polyline and the segment it lies on. */
export function closestPointOnPolyline(points: THREE.Vector3[], p: THREE.Vector3): { point: THREE.Vector3; segment: number; t: number } {
  if (points.length === 0) return { point: p.clone(), segment: 0, t: 0 };
  if (points.length === 1) return { point: points[0].clone(), segment: 0, t: 0 };
  const line = new THREE.Line3();
  const tmp = new THREE.Vector3();
  let best = { point: points[0].clone(), segment: 0, t: 0 };
  let bestD = Infinity;
  for (let i = 0; i + 1 < points.length; i++) {
    line.set(points[i], points[i + 1]);
    const t = line.closestPointToPointParameter(p, true);
    line.at(t, tmp);
    const d = tmp.distanceToSquared(p);
    if (d < bestD) {
      bestD = d;
      best = { point: tmp.clone(), segment: i, t };
    }
  }
  return best;
}

// ------------------------------------------------------------- planes

/** RootCentroidAxis.TryGetPlane: centroid and unit normal of (b - a) x (c - a). */
export function planeFromThree(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): { point: THREE.Vector3; normal: THREE.Vector3 } | null {
  const normal = new THREE.Vector3().crossVectors(b.clone().sub(a), c.clone().sub(a));
  const len = normal.length();
  if (!(len > 1e-8)) return null;
  return { point: a.clone().add(b).add(c).multiplyScalar(1 / 3), normal: normal.multiplyScalar(1 / len) };
}

/** The nadir plane with its normal flipped to agree with a prior normal (the VBR refit). */
export function nadirPlane(left: THREE.Vector3, right: THREE.Vector3, neutral: THREE.Vector3, priorNormal: THREE.Vector3 | null): { point: THREE.Vector3; normal: THREE.Vector3 } | null {
  const p = planeFromThree(left, right, neutral);
  if (!p) return null;
  if (priorNormal && p.normal.dot(priorNormal) < 0) p.normal.negate();
  return p;
}

/** BuildPlaneBasisFromNormal: X = cross(reference, n), Y = cross(n, X), reference = up unless n is near up. */
export function planeBasis(normal: THREE.Vector3): { x: THREE.Vector3; y: THREE.Vector3 } {
  const n = normal.clone().normalize();
  const reference = Math.abs(n.dot(new THREE.Vector3(0, 1, 0))) > 0.95 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
  const x = new THREE.Vector3().crossVectors(reference, n).normalize();
  const y = new THREE.Vector3().crossVectors(n, x).normalize();
  return { x, y };
}

export function projectOntoPlane(p: THREE.Vector3, point: THREE.Vector3, normal: THREE.Vector3): THREE.Vector3 {
  const d = p.clone().sub(point).dot(normal);
  return p.clone().sub(normal.clone().multiplyScalar(d));
}

// ------------------------------------------------------------- principal diameters

export interface Chord {
  a: number;
  b: number;
  length: number;
}

/** ComputePrincipalDiametersIndices on 2D ring points: antiparallel pairs about the centroid. */
export function principalDiameters(pts: [number, number][]): { max: Chord; min: Chord } {
  const n = pts.length;
  let cx = 0;
  let cy = 0;
  for (const [x, y] of pts) {
    cx += x;
    cy += y;
  }
  cx /= Math.max(1, n);
  cy /= Math.max(1, n);
  const dist = (i: number, j: number) => Math.hypot(pts[i][0] - pts[j][0], pts[i][1] - pts[j][1]);
  const valid: Chord[] = [];
  for (let i = 0; i < n; i++) {
    let v1x = pts[i][0] - cx;
    let v1y = pts[i][1] - cy;
    const n1 = Math.hypot(v1x, v1y);
    if (n1 < 1e-12) continue;
    v1x /= n1;
    v1y /= n1;
    let bestJ = -1;
    let bestScore = Infinity;
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      let v2x = pts[j][0] - cx;
      let v2y = pts[j][1] - cy;
      const n2 = Math.hypot(v2x, v2y);
      if (n2 < 1e-12) continue;
      v2x /= n2;
      v2y /= n2;
      const dot = v1x * v2x + v1y * v2y;
      if (dot < 0) {
        const score = 1 + dot;
        if (score < bestScore) {
          bestScore = score;
          bestJ = j;
        }
      }
    }
    if (bestJ >= 0 && bestScore < 0.3) valid.push({ a: i, b: bestJ, length: dist(i, bestJ) });
  }
  if (valid.length === 0) {
    const fallback: Chord[] = [];
    for (let i = 0; i < n; i++) {
      const j = (i + Math.floor(n / 2)) % n;
      fallback.push({ a: i, b: j, length: dist(i, j) });
    }
    fallback.sort((p, q) => p.length - q.length);
    return { max: fallback[fallback.length - 1], min: fallback[0] };
  }
  const seen = new Set<string>();
  const unique: Chord[] = [];
  for (const c of valid) {
    const key = `${Math.min(c.a, c.b)}|${Math.max(c.a, c.b)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(c);
  }
  unique.sort((p, q) => p.length - q.length);
  let min = unique[0];
  const max = unique[unique.length - 1];
  const vMax = [pts[max.b][0] - pts[max.a][0], pts[max.b][1] - pts[max.a][1]];
  const vMin = [pts[min.b][0] - pts[min.a][0], pts[min.b][1] - pts[min.a][1]];
  const lMax = Math.hypot(vMax[0], vMax[1]);
  const lMin = Math.hypot(vMin[0], vMin[1]);
  if (lMax > 0 && lMin > 0) {
    const dot = Math.abs((vMax[0] * vMin[0] + vMax[1] * vMin[1]) / (lMax * lMin));
    if (dot > 0.866) {
      for (const c of unique) {
        const v = [pts[c.b][0] - pts[c.a][0], pts[c.b][1] - pts[c.a][1]];
        const l = Math.hypot(v[0], v[1]);
        if (l === 0) continue;
        const perp = Math.abs((v[0] * vMax[0] + v[1] * vMax[1]) / (l * lMax));
        if (perp < 0.7) {
          min = c;
          break;
        }
      }
    }
  }
  return { max, min };
}

// ------------------------------------------------------------- root centroid axis

export const ROOT_CENTROID = {
  spacingMm: 1,
  topOvershootMm: 0.25,
  minTopAboveCoaptationMm: 2,
  maxTopAboveStjMm: 10,
  maxTopAboveCoaptationMm: 60,
  minAxisDot: 0.2,
  minCoaptationHeightMm: 0.5,
};

export interface RootCentroidLine {
  points: THREE.Vector3[];
  coaptationIndex: number;
  anchorIndices: number[];
  axis: THREE.Vector3;
  topSource: 'commissure_plane' | 'stj_height';
}

/** Math.Round half-to-even, as C# and Python round(). */
function roundHalfEven(v: number): number {
  const f = Math.floor(v);
  const diff = v - f;
  if (diff > 0.5) return f + 1;
  if (diff < 0.5) return f;
  return f % 2 === 0 ? f : f + 1;
}

/**
 * RootCentroidAxis.TryBuild: the line through the coaptation point along the VBR normal
 * (oriented superior), from the VBR plane to just past the commissure plane (or to the STJ
 * height), 1 mm vertices with the coaptation point as a vertex.
 */
export function buildRootCentroid(vbrPoint: THREE.Vector3, vbrNormal: THREE.Vector3, coaptation: THREE.Vector3, commissures: THREE.Vector3[] | null, stjCenter: THREE.Vector3 | null): RootCentroidLine | null {
  const C = ROOT_CENTROID;
  const normalLength = vbrNormal.length();
  if (!(normalLength > 1e-8)) return null;
  const axis = vbrNormal.clone().multiplyScalar(1 / normalLength);
  const superiorReference = stjCenter ?? coaptation;
  if (superiorReference.clone().sub(vbrPoint).dot(axis) < 0) axis.negate();
  const coaptationHeight = coaptation.clone().sub(vbrPoint).dot(axis);
  if (!(coaptationHeight >= C.minCoaptationHeightMm)) return null;
  const tBottom = -coaptationHeight;
  const stjHeight = stjCenter ? stjCenter.clone().sub(vbrPoint).dot(axis) : null;

  let tTop = 0;
  let topSource: RootCentroidLine['topSource'] | null = null;
  const plane = commissures && commissures.length === 3 ? planeFromThree(commissures[0], commissures[1], commissures[2]) : null;
  if (plane) {
    const denominator = axis.dot(plane.normal);
    if (Math.abs(denominator) > C.minAxisDot) {
      const tCross = plane.point.clone().sub(coaptation).dot(plane.normal) / denominator;
      const topHeight = coaptationHeight + tCross;
      const maxHeight = stjHeight !== null ? stjHeight + C.maxTopAboveStjMm : coaptationHeight + C.maxTopAboveCoaptationMm;
      if (tCross >= C.minTopAboveCoaptationMm && topHeight <= maxHeight) {
        tTop = tCross + C.topOvershootMm;
        topSource = 'commissure_plane';
      }
    }
  }
  if (topSource === null && stjHeight !== null) {
    const tCross = stjHeight - coaptationHeight;
    if (tCross >= C.minTopAboveCoaptationMm) {
      tTop = tCross;
      topSource = 'stj_height';
    }
  }
  if (topSource === null) return null;

  const below = Math.max(1, roundHalfEven(-tBottom / C.spacingMm));
  const above = Math.max(1, roundHalfEven(tTop / C.spacingMm));
  const points: THREE.Vector3[] = new Array(below + above + 1);
  for (let i = 0; i < below; i++) points[i] = coaptation.clone().add(axis.clone().multiplyScalar((tBottom * (below - i)) / below));
  points[below] = coaptation.clone();
  for (let i = 1; i <= above; i++) points[below + i] = coaptation.clone().add(axis.clone().multiplyScalar((tTop * i) / above));

  const anchors = new Set<number>([0, below, points.length - 1]);
  if (commissures && commissures.length === 3) {
    const centroid = commissures[0].clone().add(commissures[1]).add(commissures[2]).multiplyScalar(1 / 3);
    let best = 0;
    let bestD = Infinity;
    points.forEach((p, i) => {
      const d = p.distanceToSquared(centroid);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    anchors.add(best);
  }
  return { points, coaptationIndex: below, anchorIndices: [...anchors].sort((a, b) => a - b), axis, topSource };
}

// ------------------------------------------------------------- free margins

const MARGIN_INFLUENCE_PER_DELTA = 2;
const MARGIN_MIN_INFLUENCE_MM = 4;
const MARGIN_MAX_INFLUENCE_MM = 10;
const MARGIN_MAX_INFLUENCE_ARC_FRACTION = 0.3;

/** InfluenceWindowParam: normalised-arc half width of a pull by `delta`. */
export function marginInfluenceWindow(deltaMm: number, totalArcMm: number): number {
  const radius = Math.min(MARGIN_MAX_INFLUENCE_MM, Math.max(MARGIN_MIN_INFLUENCE_MM, MARGIN_INFLUENCE_PER_DELTA * deltaMm));
  return totalArcMm > 1e-7 ? Math.min(radius / totalArcMm, MARGIN_MAX_INFLUENCE_ARC_FRACTION) : 1;
}

/**
 * TryWarpFreeMarginCurveToNearestCommissures: each end goes to the commissure pair with the
 * least total travel (open ends stay), and the end delta fades out with a smootherstep over
 * the influence window so the rest of the margin keeps its shape.
 */
export function warpMarginToCommissures(points: THREE.Vector3[], commissures: THREE.Vector3[], openStart = false, openEnd = false): { points: THREE.Vector3[]; moved: boolean } {
  if (points.length < 2 || commissures.length < 2) return { points: points.map((p) => p.clone()), moved: false };
  const oldStart = points[0];
  const oldEnd = points[points.length - 1];
  let bestCost = Infinity;
  let newStart = oldStart;
  let newEnd = oldEnd;
  for (let a = 0; a < commissures.length; a++) {
    for (let b = 0; b < commissures.length; b++) {
      if (a === b) continue;
      const cost = (openStart ? 0 : oldStart.distanceTo(commissures[a])) + (openEnd ? 0 : oldEnd.distanceTo(commissures[b]));
      if (cost < bestCost) {
        bestCost = cost;
        newStart = openStart ? oldStart : commissures[a];
        newEnd = openEnd ? oldEnd : commissures[b];
      }
    }
  }
  const startDelta = newStart.clone().sub(oldStart);
  const endDelta = newEnd.clone().sub(oldEnd);
  if (startDelta.lengthSq() < 1e-12 && endDelta.lengthSq() < 1e-12) return { points: points.map((p) => p.clone()), moved: false };
  const params = arcParams(points);
  const total = polylineLength(points);
  const startWindow = marginInfluenceWindow(startDelta.length(), total);
  const endWindow = marginInfluenceWindow(endDelta.length(), total);
  const out = points.map((p, i) => {
    const s = params[i];
    return p
      .clone()
      .add(startDelta.clone().multiplyScalar(smootherstepFalloff(s, startWindow)))
      .add(endDelta.clone().multiplyScalar(smootherstepFalloff(1 - s, endWindow)));
  });
  out[0] = newStart.clone();
  out[out.length - 1] = newEnd.clone();
  return { points: out, moved: true };
}

// ------------------------------------------------------------- VBR ring through the nadirs

const VBR_PROFILE_BINS = 128;
const VBR_LUMEN_HARMONICS = 5;
const VBR_NADIR_RESIDUAL_HARMONICS = 4;
const VBR_FINAL_HARMONICS = 5;
const VBR_NADIR_MAX_RESIDUAL_FRACTION = 0.65;
const VBR_NADIR_RESIDUAL_HALF_WIDTH = 0.7854;
const VBR_NADIR_EXACT_BLEND_RADIANS = 0.22;

function repeat(a: number, m: number): number {
  return a - Math.floor(a / m) * m;
}

function shortestAngularDistance(a: number, b: number): number {
  const d = Math.abs(repeat(a - b + Math.PI, TWO_PI) - Math.PI);
  return d;
}

function toPolar(p: THREE.Vector3, center: THREE.Vector3, px: THREE.Vector3, py: THREE.Vector3): { angle: number; radius: number } | null {
  const d = p.clone().sub(center);
  const x = d.dot(px);
  const y = d.dot(py);
  const radius = Math.hypot(x, y);
  if (radius < 1e-7) return null;
  return { angle: Math.atan2(y, x), radius };
}

function sampleRadius(radii: number[], angle: number): number {
  const n = radii.length;
  const t = (repeat(angle, TWO_PI) / TWO_PI) * n - 0.5;
  const i0 = Math.floor(t);
  const frac = t - i0;
  const a = ((i0 % n) + n) % n;
  const b = (a + 1) % n;
  return radii[a] + (radii[b] - radii[a]) * frac;
}

/** LowPassFourier: keep the mean and the first `harmonics` terms. */
export function lowPassFourier(radii: number[], harmonics: number): number[] {
  const n = radii.length;
  if (n === 0) return radii;
  const k = Math.min(Math.max(1, harmonics), Math.floor(n / 2));
  let mean = 0;
  for (const r of radii) mean += r;
  mean /= n;
  const a = new Array<number>(k + 1).fill(0);
  const b = new Array<number>(k + 1).fill(0);
  for (let h = 1; h <= k; h++) {
    let ca = 0;
    let cb = 0;
    for (let i = 0; i < n; i++) {
      const theta = ((i + 0.5) / n) * TWO_PI;
      ca += radii[i] * Math.cos(h * theta);
      cb += radii[i] * Math.sin(h * theta);
    }
    a[h] = (2 / n) * ca;
    b[h] = (2 / n) * cb;
  }
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const theta = ((i + 0.5) / n) * TWO_PI;
    let r = mean;
    for (let h = 1; h <= k; h++) r += a[h] * Math.cos(h * theta) + b[h] * Math.sin(h * theta);
    out[i] = r;
  }
  return out;
}

/** ResamplePolarRadii without outlier rejection: periodic linear interpolation per bin. */
function resampleContourToRadii(points: THREE.Vector3[], center: THREE.Vector3, px: THREE.Vector3, py: THREE.Vector3, bins: number): number[] | null {
  const polar: { angle: number; radius: number }[] = [];
  for (const p of points) {
    const q = toPolar(p, center, px, py);
    if (q) polar.push({ angle: repeat(q.angle, TWO_PI), radius: q.radius });
  }
  if (polar.length < bins / 4) return null;
  polar.sort((a, b) => a.angle - b.angle);
  const out = new Array<number>(bins);
  for (let i = 0; i < bins; i++) {
    const theta = ((i + 0.5) / bins) * TWO_PI;
    // bracketing samples around theta, wrapping
    let hi = polar.findIndex((s) => s.angle >= theta);
    let lo: number;
    let span: number;
    let t: number;
    if (hi === -1) {
      hi = 0;
      lo = polar.length - 1;
      span = polar[hi].angle + TWO_PI - polar[lo].angle;
      t = span > 1e-9 ? (theta - polar[lo].angle) / span : 0;
    } else if (hi === 0) {
      lo = polar.length - 1;
      span = polar[hi].angle + TWO_PI - polar[lo].angle;
      t = span > 1e-9 ? (theta + TWO_PI - polar[lo].angle) / span : 0;
    } else {
      lo = hi - 1;
      span = polar[hi].angle - polar[lo].angle;
      t = span > 1e-9 ? (theta - polar[lo].angle) / span : 0;
    }
    out[i] = polar[lo].radius + (polar[hi].radius - polar[lo].radius) * Math.min(1, Math.max(0, t));
  }
  return out;
}

function tightLumenRadii(lumen: number[], anchorAngles: number[], anchorRadii: number[]): number[] {
  const n = lumen.length;
  const residual = new Array<number>(n).fill(0);
  const weight = new Array<number>(n).fill(0);
  for (let a = 0; a < anchorAngles.length; a++) {
    const local = sampleRadius(lumen, anchorAngles[a]);
    let target = anchorRadii[a] - local;
    const maxDelta = Math.max(local * VBR_NADIR_MAX_RESIDUAL_FRACTION, 0.5);
    target = Math.min(maxDelta, Math.max(-maxDelta, target));
    for (let i = 0; i < n; i++) {
      const theta = ((i + 0.5) / n) * TWO_PI;
      const d = shortestAngularDistance(theta, anchorAngles[a]);
      if (d >= VBR_NADIR_RESIDUAL_HALF_WIDTH) continue;
      let w = Math.cos((d / VBR_NADIR_RESIDUAL_HALF_WIDTH) * Math.PI * 0.5);
      w *= w;
      residual[i] += target * w;
      weight[i] += w;
    }
  }
  let delta = residual.map((r, i) => (weight[i] > 1e-6 ? r / weight[i] : 0));
  delta = lowPassFourier(delta, VBR_NADIR_RESIDUAL_HARMONICS);
  const result = lumen.map((r, i) => Math.max(r + delta[i], 1e-4));
  return lowPassFourier(result, VBR_FINAL_HARMONICS);
}

function softBlendToAnchors(radius: number, angle: number, anchorAngles: number[], anchorRadii: number[]): number {
  let r = radius;
  for (let a = 0; a < anchorAngles.length; a++) {
    const d = shortestAngularDistance(angle, anchorAngles[a]);
    if (d >= VBR_NADIR_EXACT_BLEND_RADIANS) continue;
    let w = Math.cos((d / VBR_NADIR_EXACT_BLEND_RADIANS) * Math.PI * 0.5);
    w *= w;
    r = r + (anchorRadii[a] - r) * w;
  }
  return Math.max(r, 1e-4);
}

/**
 * TryDeformRingContourThroughNadirsWorld: project the pipeline ring onto the nadir plane,
 * resample it as a radius profile, pull the profile through the nadirs with narrow cosine
 * residuals, and put the exact nadir positions on the nearest samples. (The desktop's isolated
 * bulge suppression is skipped: pipeline contours are already smooth.)
 */
export function deformRingThroughNadirs(baseRing: THREE.Vector3[], left: THREE.Vector3, right: THREE.Vector3, neutral: THREE.Vector3, centerHint: THREE.Vector3 | null): { ring: THREE.Vector3[]; center: THREE.Vector3; normal: THREE.Vector3 } | null {
  if (baseRing.length < 8) return null;
  const normal = new THREE.Vector3().crossVectors(right.clone().sub(left), neutral.clone().sub(left));
  if (normal.lengthSq() < 1e-10) return null;
  normal.normalize();
  const planeOrigin = left.clone().add(right).add(neutral).multiplyScalar(1 / 3);
  const center = centerHint ? projectOntoPlane(centerHint, planeOrigin, normal) : planeOrigin.clone();
  const { x: px, y: py } = planeBasis(normal);
  const projected = baseRing.map((p) => projectOntoPlane(p, center, normal));
  let radii = resampleContourToRadii(projected, center, px, py, VBR_PROFILE_BINS);
  if (!radii) return null;
  radii = lowPassFourier(radii, VBR_LUMEN_HARMONICS);

  const anchors = [left, right, neutral]
    .map((a) => toPolar(a, center, px, py))
    .filter((q): q is { angle: number; radius: number } => !!q && q.radius >= 1e-6)
    .map((q) => ({ angle: repeat(q.angle, TWO_PI), radius: q.radius }))
    .sort((a, b) => a.angle - b.angle);
  if (anchors.length === 0) return null;
  const anchorAngles = anchors.map((a) => a.angle);
  const anchorRadii = anchors.map((a) => a.radius);
  const finalRadii = tightLumenRadii(radii, anchorAngles, anchorRadii);

  const n = finalRadii.length;
  const ring: THREE.Vector3[] = [];
  for (let i = 0; i < n; i++) {
    const ang = ((i + 0.5) / n) * TWO_PI;
    let r = Math.max(sampleRadius(finalRadii, ang), 1e-4);
    r = softBlendToAnchors(r, ang, anchorAngles, anchorRadii);
    ring.push(center.clone().add(px.clone().multiplyScalar(Math.cos(ang) * r)).add(py.clone().multiplyScalar(Math.sin(ang) * r)));
  }
  // exact nadirs on the closest angular samples
  const claimed = new Set<number>();
  for (const nadir of [left, right, neutral]) {
    const q = toPolar(nadir, center, px, py);
    if (!q) continue;
    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < ring.length; i++) {
      if (claimed.has(i)) continue;
      const s = toPolar(ring[i], center, px, py);
      if (!s) continue;
      const d = shortestAngularDistance(s.angle, q.angle);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best >= 0) {
      ring[best] = nadir.clone();
      claimed.add(best);
    }
  }
  return { ring, center, normal };
}

// ------------------------------------------------------------- splines

/**
 * BezierLine.SmoothLine: open centripetal Catmull-Rom (Barry-Goldman, alpha 0.5) with the
 * end points duplicated, `resolution` samples per segment, the last control point appended.
 */
export function catmullRomOpen(control: THREE.Vector3[], resolution = 10, alpha = 0.5): THREE.Vector3[] {
  if (control.length < 2) return control.map((p) => p.clone());
  const pts = [control[0], ...control, control[control.length - 1]];
  const out: THREE.Vector3[] = [];
  const tj = (t: number, a: THREE.Vector3, b: THREE.Vector3) => t + Math.pow(Math.max(1e-4, a.distanceTo(b)), alpha);
  const lerp = (a: THREE.Vector3, b: THREE.Vector3, t0: number, t1: number, t: number) => (Math.abs(t1 - t0) < 1e-6 ? a.clone() : a.clone().lerp(b, (t - t0) / (t1 - t0)));
  for (let i = 0; i + 3 < pts.length; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const p2 = pts[i + 2];
    const p3 = pts[i + 3];
    const t0 = 0;
    const t1 = tj(t0, p0, p1);
    const t2 = tj(t1, p1, p2);
    const t3 = tj(t2, p2, p3);
    for (let j = 0; j < resolution; j++) {
      const t = t1 + ((t2 - t1) * j) / resolution;
      const a1 = lerp(p0, p1, t0, t1, t);
      const a2 = lerp(p1, p2, t1, t2, t);
      const a3 = lerp(p2, p3, t2, t3, t);
      const b1 = lerp(a1, a2, t0, t2, t);
      const b2 = lerp(a2, a3, t1, t3, t);
      out.push(lerp(b1, b2, t1, t2, t));
    }
  }
  out.push(control[control.length - 1].clone());
  return out;
}
